import { select } from 'd3-selection';
import 'd3-transition';
import { zoom, zoomIdentity, type ZoomBehavior, type ZoomTransform } from 'd3-zoom';
import { useEffect, useRef, useState, type MouseEvent } from 'react';

import { padBox, type BBox } from '@/papermill/extraction';

import styles from './PageZoom.module.css';

export interface PageZoomProps {
  /** The page as an image, and its size in pixels. */
  src: string;
  width: number;
  height: number;
  bbox: BBox;
  label: string;
}

const MINIMAP_WIDTH = 112;

/** A page you can zoom and pan, opened on the box a value was read from, with a mini-map. */
export function PageZoom({ src, width, height, bbox, label }: PageZoomProps) {
  const viewportRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const zoomRef = useRef<ZoomBehavior<HTMLDivElement, unknown> | null>(null);
  const [view, setView] = useState<{
    k: number;
    x: number;
    y: number;
    vw: number;
    vh: number;
  } | null>(null);
  const box = padBox(bbox);
  const mark = {
    left: box.x0 * width,
    top: box.y0 * height,
    width: (box.x1 - box.x0) * width,
    height: (box.y1 - box.y0) * height,
  };

  function focusTransform(target: 'box' | 'page'): ZoomTransform | null {
    const viewport = viewportRef.current;
    if (!viewport) {
      return null;
    }
    const vw = viewport.clientWidth;
    const vh = viewport.clientHeight;
    if (target === 'page') {
      const k = Math.min(vw / width, vh / height);
      return zoomIdentity.translate((vw - width * k) / 2, (vh - height * k) / 2).scale(k);
    }
    const regionW = Math.max(mark.width * 2.6, width * 0.32);
    const regionH = Math.max(mark.height * 5, regionW * (vh / vw));
    const k = Math.min(vw / regionW, vh / regionH, 4);
    const cx = mark.left + mark.width / 2;
    const cy = mark.top + mark.height / 2;
    return zoomIdentity
      .translate(vw / 2, vh / 2)
      .scale(k)
      .translate(-cx, -cy);
  }

  function go(target: 'box' | 'page', animate = true) {
    const viewport = viewportRef.current;
    const transform = focusTransform(target);
    if (!viewport || !transform || !zoomRef.current) {
      return;
    }
    const selection = select(viewport);
    if (animate) {
      selection.transition().duration(550).call(zoomRef.current.transform, transform);
    } else {
      selection.call(zoomRef.current.transform, transform);
    }
  }

  useEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) {
      return;
    }
    const fit = Math.min(viewport.clientWidth / width, viewport.clientHeight / height);
    const behavior = zoom<HTMLDivElement, unknown>()
      .scaleExtent([fit * 0.8, 6])
      .on('zoom', (event) => {
        const { k, x, y } = event.transform as ZoomTransform;
        content.style.transform = `translate(${x}px, ${y}px) scale(${k})`;
        setView({ k, x, y, vw: viewport.clientWidth, vh: viewport.clientHeight });
      });
    zoomRef.current = behavior;
    select(viewport).call(behavior).on('dblclick.zoom', null);
    return () => {
      select(viewport).on('.zoom', null);
    };
  }, [width, height]);

  const boxKey = `${bbox.page}:${bbox.x0}:${bbox.y0}:${bbox.x1}:${bbox.y1}`;
  const focused = useRef(false);
  useEffect(() => {
    go('box', focused.current);
    focused.current = true;
    // Focus follows the box; re-running for other state would fight the reader's panning.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boxKey, src]);

  const scale = MINIMAP_WIDTH / width;
  const minimapHeight = height * scale;
  const visible = view
    ? {
        left: Math.max(0, (-view.x / view.k) * scale),
        top: Math.max(0, (-view.y / view.k) * scale),
        width: Math.min(MINIMAP_WIDTH, (view.vw / view.k) * scale),
        height: Math.min(minimapHeight, (view.vh / view.k) * scale),
      }
    : null;

  function onMinimap(event: MouseEvent<HTMLButtonElement>) {
    const viewport = viewportRef.current;
    if (!viewport || !zoomRef.current || !view) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const px = (event.clientX - rect.left) / scale;
    const py = (event.clientY - rect.top) / scale;
    const transform = zoomIdentity
      .translate(view.vw / 2, view.vh / 2)
      .scale(view.k)
      .translate(-px, -py);
    select(viewport).transition().duration(350).call(zoomRef.current.transform, transform);
  }

  return (
    <div className={styles.frame}>
      <div
        ref={viewportRef}
        className={styles.viewport}
        aria-label={`Page ${bbox.page + 1}, zoomable`}
      >
        <div ref={contentRef} className={styles.content} style={{ width, height }}>
          <img
            src={src}
            alt={`Page ${bbox.page + 1}`}
            width={width}
            height={height}
            draggable={false}
          />
          <div
            key={boxKey}
            className={styles.mark}
            style={{ ...mark, borderWidth: `${Math.max(2, 2.5 / (view?.k ?? 1))}px` }}
            role="img"
            aria-label={`Where “${label}” was read`}
          />
        </div>
      </div>

      <div className={styles.controls}>
        <button
          type="button"
          aria-label="Zoom in"
          onClick={() =>
            zoomRef.current &&
            select(viewportRef.current!)
              .transition()
              .duration(200)
              .call(zoomRef.current.scaleBy, 1.4)
          }
        >
          +
        </button>
        <button
          type="button"
          aria-label="Zoom out"
          onClick={() =>
            zoomRef.current &&
            select(viewportRef.current!)
              .transition()
              .duration(200)
              .call(zoomRef.current.scaleBy, 1 / 1.4)
          }
        >
          −
        </button>
        <button type="button" aria-label="Zoom to the value" onClick={() => go('box')}>
          ◎
        </button>
        <button type="button" aria-label="Whole page" onClick={() => go('page')}>
          ⤢
        </button>
      </div>

      <button
        type="button"
        className={styles.minimap}
        style={{ width: MINIMAP_WIDTH, height: minimapHeight }}
        onClick={onMinimap}
        aria-label="Mini-map: click to move there"
      >
        <img src={src} alt="" width={MINIMAP_WIDTH} height={minimapHeight} draggable={false} />
        <span
          className={styles.miniMark}
          style={{
            left: mark.left * scale,
            top: mark.top * scale,
            width: Math.max(3, mark.width * scale),
            height: Math.max(3, mark.height * scale),
          }}
        />
        {visible ? <span className={styles.miniView} style={visible} /> : null}
      </button>
    </div>
  );
}
